// ante-react-kit/src/components/AntChangePasscodePopup/index.js
// Popup đổi PIN 3 bước (1 ô PIN mỗi lần): cũ → mới → xác nhận.
// Target tự suy luận: đang phiên ACUserEmployee (có employee_code) → 'employee', else → 'user'.
// FE gửi target trong payload để BE biết đổi PIN cho ACUserAccount hay ACUserEmployee.
import React, { Fragment, useState } from 'react';
import AntPasscodeOverlay from '../AntPasscodeOverlay';
import AntAuth from '../../utils/AntAuth';

const STEP_OLD = 'OLD';
const STEP_NEW = 'NEW';
const STEP_CONFIRM = 'CONFIRM';

export default function AntChangePasscodePopup(props) {
    const { onClose, onChanged } = props;

    const employeeCode = AntAuth.getSessionInfo && AntAuth.getSessionInfo('employee_code');
    const employeeName = AntAuth.getSessionInfo && AntAuth.getSessionInfo('employee_name');
    const userName = AntAuth.getSessionInfo && AntAuth.getSessionInfo('user_name');

    // Tự xác định target — không cho user chọn.
    const target = employeeCode ? 'employee' : 'user';

    const [step, setStep] = useState(STEP_OLD);
    const [oldPin, setOldPin] = useState('');
    const [newPin, setNewPin] = useState('');
    const [errorText, setErrorText] = useState('');
    const [errorKey, setErrorKey] = useState(0);
    const [processing, setProcessing] = useState(false);

    const fail = (msg) => {
        setErrorText(msg);
        setErrorKey((k) => k + 1);
    };

    const handleComplete = (pin) => {
        setErrorText('');
        if (step === STEP_OLD) {
            // Verify PIN cũ NGAY tại STEP_OLD — KHÔNG cho qua bước 2 nếu sai.
            setProcessing(true);
            AntAuth.verifyOldPasscode({ target, pass_code: pin }, (res) => {
                setProcessing(false);
                if (!res || res.error || !res.data || !res.data.valid) {
                    const msgObj = res && res.error && res.error.message;
                    const msg = (msgObj && (msgObj.vi || msgObj.en)) || 'PIN hiện tại không đúng';
                    fail(msg);
                    return;
                }
                setOldPin(pin);
                setStep(STEP_NEW);
            });
            return;
        }
        if (step === STEP_NEW) {
            if (pin === oldPin) {
                fail('PIN mới phải khác PIN hiện tại');
                return;
            }
            setNewPin(pin);
            setStep(STEP_CONFIRM);
            return;
        }
        // STEP_CONFIRM
        if (pin !== newPin) {
            // Quay lại bước nhập PIN mới — để user gõ lại đầy đủ.
            setNewPin('');
            setStep(STEP_NEW);
            fail('Xác nhận không khớp. Hãy nhập lại PIN mới.');
            return;
        }
        setProcessing(true);
        AntAuth.changePasscode({ target, old_pass_code: oldPin, new_pass_code: pin }, (res) => {
            setProcessing(false);
            if (!res || res.error || !res.data || !res.data.changed) {
                const msgObj = res && res.error && res.error.message;
                const msg = (msgObj && (msgObj.vi || msgObj.en)) || 'Đổi PIN thất bại. Vui lòng thử lại.';
                // Nếu lỗi PIN cũ → quay về STEP_OLD; nếu lỗi PIN mới (định dạng) → STEP_NEW.
                if (res && res.error && res.error.code === 'invalid_old_passcode') {
                    setOldPin('');
                    setStep(STEP_OLD);
                } else if (res && res.error && res.error.code === 'invalid_new_passcode') {
                    setNewPin('');
                    setStep(STEP_NEW);
                }
                fail(msg);
                return;
            }
            if (onChanged) onChanged(res.data);
        });
    };

    const titleByStep = {
        [STEP_OLD]: 'Nhập PIN hiện tại',
        [STEP_NEW]: 'Nhập PIN mới',
        [STEP_CONFIRM]: 'Xác nhận PIN mới'
    };
    const subtitleByStep = {
        [STEP_OLD]: 'Bước 1/3',
        [STEP_NEW]: 'Bước 2/3',
        [STEP_CONFIRM]: 'Bước 3/3'
    };
    const pinLabelByStep = {
        [STEP_OLD]: 'PIN hiện tại',
        [STEP_NEW]: 'PIN mới (6 chữ số)',
        [STEP_CONFIRM]: 'Nhập lại PIN mới'
    };

    const targetLine =
        target === 'employee'
            ? `Đổi PIN nhân viên: ${employeeName || employeeCode}`
            : `Đổi PIN tài khoản: ${userName || ''}`;

    const topSlot = (
        <div className="apt-change-pin-top">
            <div className="apt-change-pin-target">{targetLine}</div>
        </div>
    );

    const handleBack = () => {
        setErrorText('');
        if (step === STEP_NEW) {
            setOldPin('');
            setStep(STEP_OLD);
        } else if (step === STEP_CONFIRM) {
            setNewPin('');
            setStep(STEP_NEW);
        }
    };

    const bottomActions = (
        <Fragment>
            {step !== STEP_OLD && (
                <button type="button" className="apt-passcode-secondary-btn" onClick={handleBack}>
                    Quay lại
                </button>
            )}
            <button type="button" className="apt-passcode-secondary-btn" onClick={onClose}>
                Huỷ
            </button>
        </Fragment>
    );

    // key={step:errorKey} → re-mount AntPasscodeOverlay khi đổi step.
    // Nếu không, state value nội bộ của overlay sẽ giữ PIN của bước trước → ô PIN
    // hiện đã đầy 6 dấu chấm dù user mới chuyển bước → UX sai, có thể không gõ thêm được.
    return (
        <AntPasscodeOverlay
            key={`${step}:${errorKey}`}
            length={6}
            title={titleByStep[step]}
            subtitle={subtitleByStep[step]}
            pinLabel={pinLabelByStep[step]}
            topSlot={topSlot}
            bottomActions={bottomActions}
            processing={processing}
            errorText={errorText}
            errorKey={errorKey}
            onComplete={handleComplete}
            dismissible={false}
        />
    );
}
